import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import UploadZone from "../components/UploadZone";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { FileAudio, FileVideo, Link2, Loader2, Trash2, Globe2 } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
    const { user, loading } = useAuth();
    const nav = useNavigate();
    const [items, setItems] = useState([]);
    const [usage, setUsage] = useState(null);
    const [fetching, setFetching] = useState(true);

    useEffect(() => {
        if (!loading && !user) nav("/auth");
    }, [loading, user, nav]);

    const refresh = async () => {
        setFetching(true);
        try {
            const [t, u] = await Promise.all([
                api.get("/transcripts"),
                api.get("/usage"),
            ]);
            setItems(t.data);
            setUsage(u.data);
        } catch (e) {
            toast.error("Failed to load", { description: e?.response?.data?.detail || e.message });
        } finally {
            setFetching(false);
        }
    };

    useEffect(() => {
        if (user) refresh();
    }, [user]);

    const onCreated = (newItem) => {
        setItems((prev) => [newItem, ...prev]);
        refresh();
        nav(`/app/t/${newItem.id}`);
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this transcript?")) return;
        try {
            await api.delete(`/transcripts/${id}`);
            setItems((prev) => prev.filter((x) => x.id !== id));
            toast.success("Deleted");
        } catch (e) {
            toast.error("Delete failed");
        }
    };

    if (loading || !user) return null;

    const minutesPct = usage ? Math.min(100, (usage.minutes_used / usage.minutes_limit) * 100) : 0;

    return (
        <div className="min-h-screen bg-white">
            <Header />
            <main className="max-w-5xl mx-auto px-6 py-12 sm:py-16">
                {/* Greeting + usage */}
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
                    <div>
                        <p className="text-xs font-medium text-slate-400 uppercase tracking-widest">Dashboard</p>
                        <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900" data-testid="dashboard-title">
                            Hi, {user.name?.split(" ")[0] || "there"}.
                        </h1>
                        <p className="mt-2 text-slate-500">Drop a file or paste a link to get started.</p>
                    </div>
                    {usage && (
                        <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 min-w-[260px]" data-testid="usage-card">
                            <div className="flex items-baseline justify-between text-sm">
                                <span className="text-slate-500 inline-flex items-center gap-2">
                                    This month
                                    {usage.subscription_status === "pro" && (
                                        <span className="inline-flex items-center rounded-full bg-indigo-50 border border-indigo-100 px-2 py-0.5 text-[10px] font-medium text-indigo-700">
                                            {usage.subscription_plan === "yearly" ? "Pro · Yearly" : "Pro · Monthly"}
                                        </span>
                                    )}
                                </span>
                                <span className="font-medium text-slate-900">
                                    {usage.minutes_used.toFixed(1)} / {usage.minutes_limit >= 999999 ? "∞" : usage.minutes_limit} min
                                </span>
                            </div>
                            <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full bg-indigo-600 rounded-full transition-all" style={{ width: `${minutesPct}%` }} />
                            </div>
                            {usage.subscription_status !== "pro" && (
                                <Link
                                    to="/pricing"
                                    className="mt-3 inline-flex items-center justify-center rounded-lg bg-slate-900 hover:bg-slate-800 transition-colors text-white text-xs font-medium px-3 py-1.5 w-full"
                                    data-testid="upgrade-cta"
                                >
                                    Upgrade for more minutes →
                                </Link>
                            )}
                        </div>
                    )}
                </div>

                {/* Upload zone */}
                <UploadZone onCreated={onCreated} />

                {/* Recent list */}
                <div className="mt-16">
                    <h2 className="text-lg font-medium text-slate-900 tracking-tight mb-5">Recent transcripts</h2>

                    {fetching ? (
                        <div className="flex items-center justify-center py-20 text-slate-400" data-testid="loading-state">
                            <Loader2 className="w-5 h-5 animate-spin" />
                        </div>
                    ) : items.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-slate-200 py-20 text-center" data-testid="empty-state">
                            <div className="w-12 h-12 mx-auto rounded-xl bg-slate-50 flex items-center justify-center">
                                <FileAudio className="w-6 h-6 text-slate-400" strokeWidth={1.5} />
                            </div>
                            <p className="mt-4 text-sm text-slate-500">No transcripts yet. Upload your first file above.</p>
                        </div>
                    ) : (
                        <ul className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="transcripts-list">
                            {items.map((t) => (
                                <li key={t.id} className="group">
                                    <Link
                                        to={`/app/t/${t.id}`}
                                        className="flex items-center gap-4 px-5 py-4 hover:bg-slate-50 transition-colors"
                                        data-testid={`transcript-item-${t.id}`}
                                    >
                                        <div className="w-9 h-9 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center shrink-0">
                                            {t.source_type === "url" ? (
                                                <Link2 className="w-4 h-4 text-slate-500" strokeWidth={1.75} />
                                            ) : t.source_name?.match(/\.(mp4|webm|mov)$/i) ? (
                                                <FileVideo className="w-4 h-4 text-slate-500" strokeWidth={1.75} />
                                            ) : (
                                                <FileAudio className="w-4 h-4 text-slate-500" strokeWidth={1.75} />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-slate-900 truncate">{t.source_name}</p>
                                            <div className="mt-0.5 flex items-center gap-3 text-xs text-slate-400">
                                                <span>{new Date(t.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</span>
                                                {t.duration_seconds > 0 && (
                                                    <span className="font-mono">{formatDuration(t.duration_seconds)}</span>
                                                )}
                                                {t.language && (
                                                    <span className="inline-flex items-center gap-1"><Globe2 className="w-3 h-3" strokeWidth={1.75} />{t.language}</span>
                                                )}
                                                <StatusBadge status={t.status} />
                                            </div>
                                        </div>
                                        <button
                                            onClick={(e) => { e.preventDefault(); remove(t.id); }}
                                            className="opacity-0 group-hover:opacity-100 p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                                            data-testid={`delete-${t.id}`}
                                        >
                                            <Trash2 className="w-4 h-4" strokeWidth={1.75} />
                                        </button>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </main>
        </div>
    );
}

function StatusBadge({ status }) {
    const map = {
        done: { label: "Done", c: "text-emerald-700 bg-emerald-50 border-emerald-100" },
        processing: { label: "Processing", c: "text-amber-700 bg-amber-50 border-amber-100" },
        error: { label: "Error", c: "text-red-700 bg-red-50 border-red-100" },
    };
    const s = map[status] || map.done;
    return <span className={`inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium border ${s.c}`}>{s.label}</span>;
}

function formatDuration(sec) {
    sec = Math.round(sec);
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
}
